/* =============================================================================
   NORVA — site behaviour
   No dependencies. Everything degrades gracefully if an element is absent.

   Motion policy:
   - Only `transform` and `opacity` are animated (no layout-triggering props).
   - Scroll work is batched into requestAnimationFrame.
   - `prefers-reduced-motion: reduce` short-circuits every animation and jumps
     straight to the end state.
   ========================================================================== */
(function () {
  "use strict";

  var motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  var reduced = motionQuery.matches;
  motionQuery.addEventListener("change", function (e) {
    reduced = e.matches;
  });

  var $ = function (sel, root) {
    return (root || document).querySelector(sel);
  };
  var $$ = function (sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  };

  /* -------------------------------------------------------------------------
     1. Scroll reveal
     Elements inside [data-reveal-group] are staggered 70ms apart, capped so a
     long list never waits more than ~560ms for its last item.
     ---------------------------------------------------------------------- */
  function initReveal() {
    var items = $$("[data-reveal]");
    if (!items.length) return;

    if (reduced || !("IntersectionObserver" in window)) {
      items.forEach(function (el) {
        el.classList.add("is-in");
      });
      $$(".line-mask").forEach(function (el) {
        el.classList.add("is-in");
      });
      return;
    }

    $$("[data-reveal-group]").forEach(function (group) {
      var step = parseInt(group.getAttribute("data-reveal-step"), 10) || 70;
      $$("[data-reveal]", group).forEach(function (el, i) {
        el.style.setProperty("--reveal-delay", Math.min(i * step, 560) + "ms");
      });
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          io.unobserve(entry.target);
        });
      },
      // threshold 0, not a fraction of the element: a fractional threshold is
      // unreachable for anything taller than the viewport. The buy card is
      // ~1600px tall, so 12% of it was 195px, while only ~120px of it sits
      // above the fold on a phone — the product sat invisible until you
      // scrolled. The negative bottom margin is what defines "entered" now.
      { rootMargin: "0px 0px -12% 0px", threshold: 0 }
    );

    // Whatever is already on screen when the page loads reveals immediately,
    // so nothing above the fold waits on a scroll that may never come. Note
    // this uses the whole viewport, not the observer's shortened root: an
    // element only just peeking in at the bottom is still something the
    // visitor can see, and leaving it blank is the bug we are fixing.
    var fold = window.innerHeight;
    items.forEach(function (el) {
      if (el.getBoundingClientRect().top < fold) {
        el.classList.add("is-in");
      } else {
        io.observe(el);
      }
    });
  }

  /* -------------------------------------------------------------------------
     2. Hero headline — masked line reveal on load
     ---------------------------------------------------------------------- */
  function initHeroLines() {
    var lines = $$("[data-hero-line]");
    if (!lines.length) return;
    if (reduced) {
      lines.forEach(function (l) {
        l.classList.add("is-in");
      });
      return;
    }
    lines.forEach(function (line, i) {
      line.style.setProperty("--reveal-delay", 120 + i * 110 + "ms");
    });
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        lines.forEach(function (l) {
          l.classList.add("is-in");
        });
      });
    });
  }

  /* -------------------------------------------------------------------------
     3. Header: translucent once scrolled, hides on scroll-down
     4. Sticky buy bar: appears once the hero CTA has left the viewport
     ---------------------------------------------------------------------- */
  function initScrollChrome() {
    var header = $("[data-header]");
    var buybar = $("[data-buybar]");
    var sentinel = $("[data-buybar-sentinel]");
    if (!header && !buybar) return;

    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;

      if (header) {
        header.setAttribute("data-scrolled", y > 24 ? "true" : "false");
        // Never hide while a menu is open or near the top of the page.
        var menuOpen = header.getAttribute("data-menu-open") === "true";
        var goingDown = y > lastY + 4;
        var goingUp = y < lastY - 4;
        if (menuOpen || y < 160) {
          header.setAttribute("data-hidden", "false");
        } else if (goingDown) {
          header.setAttribute("data-hidden", "true");
        } else if (goingUp) {
          header.setAttribute("data-hidden", "false");
        }
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();

    if (buybar && sentinel && "IntersectionObserver" in window) {
      var bio = new IntersectionObserver(
        function (entries) {
          var e = entries[0];
          // Only once the hero CTA has scrolled off the TOP — not while it is
          // still below the fold on a tall phone.
          var scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
          buybar.setAttribute("data-shown", scrolledPast ? "true" : "false");
        },
        { rootMargin: "0px" }
      );
      bio.observe(sentinel);
    }
  }

  /* -------------------------------------------------------------------------
     5. Mobile navigation
     ---------------------------------------------------------------------- */
  function initNav() {
    var header = $("[data-header]");
    var toggle = $("[data-nav-toggle]");
    var panel = $("[data-nav-panel]");
    if (!toggle || !panel || !header) return;

    var closeBtn = $("[data-nav-close]", panel);

    function isOpen() {
      return toggle.getAttribute("aria-expanded") === "true";
    }

    function setOpen(open) {
      toggle.setAttribute("aria-expanded", String(open));
      panel.hidden = !open;
      header.setAttribute("data-menu-open", String(open));
      document.documentElement.classList.toggle("no-scroll", open);
      if (open) {
        var first = panel.querySelector("a, button");
        if (first) first.focus();
      }
    }

    toggle.addEventListener("click", function () {
      setOpen(!isOpen());
    });

    if (closeBtn) {
      closeBtn.addEventListener("click", function () {
        setOpen(false);
        toggle.focus();
      });
    }

    // Escape route out of the menu (a11y: escape-routes)
    document.addEventListener("keydown", function (e) {
      if (!isOpen()) return;

      if (e.key === "Escape") {
        setOpen(false);
        toggle.focus();
        return;
      }

      // Keep Tab inside the overlay while it is open (aria-modal="true")
      if (e.key === "Tab") {
        var focusables = $$("a[href], button:not([disabled])", panel);
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    $$("a", panel).forEach(function (a) {
      a.addEventListener("click", function () {
        setOpen(false);
      });
    });

    // Reset when we grow past the mobile breakpoint
    window.matchMedia("(min-width: 900px)").addEventListener("change", function (e) {
      if (e.matches) setOpen(false);
    });
  }

  /* -------------------------------------------------------------------------
     6. FAQ accordion — one open at a time, fully keyboard operable
     ---------------------------------------------------------------------- */
  function initAccordion() {
    var items = $$("[data-acc-item]");
    if (!items.length) return;

    /* Animate to an explicit pixel height, then hand back to CSS (auto / 0) so
       the panel reflows correctly afterwards — clearing the inline height lets
       the panel grow if its content reflows (font swap, resize, translation).
       Inline styles win while the transition runs. */
    function collapse(item) {
      var panel = $("[data-acc-content]", item);
      if (!panel || item.getAttribute("data-open") !== "true") return;
      panel.style.height = panel.scrollHeight + "px";
      void panel.offsetHeight; // force reflow so the next value animates
      item.setAttribute("data-open", "false");
      panel.style.height = "0px";
      var b = $("[data-acc-trigger]", item);
      if (b) b.setAttribute("aria-expanded", "false");
      panel.setAttribute("aria-hidden", "true");
    }

    function expand(item) {
      var panel = $("[data-acc-content]", item);
      var btn = $("[data-acc-trigger]", item);
      if (!panel) return;
      item.setAttribute("data-open", "true");
      if (btn) btn.setAttribute("aria-expanded", "true");
      panel.setAttribute("aria-hidden", "false");
      panel.style.height = "0px";
      void panel.offsetHeight;
      panel.style.height = panel.scrollHeight + "px";
    }

    items.forEach(function (item) {
      var btn = $("[data-acc-trigger]", item);
      var panel = $("[data-acc-content]", item);
      if (!btn || !panel) return;

      // Once settled, drop the inline height so content can reflow freely
      panel.addEventListener("transitionend", function (e) {
        if (e.propertyName !== "height") return;
        panel.style.height = "";
      });

      btn.addEventListener("click", function () {
        var isOpen = item.getAttribute("data-open") === "true";
        items.forEach(collapse);
        if (!isOpen) expand(item);
      });
    });
  }

  /* -------------------------------------------------------------------------
     7. Product anatomy hotspots
     ---------------------------------------------------------------------- */
  function initHotspots() {
    var spots = $$("[data-hotspot]");
    if (!spots.length) return;

    var stage = $("[data-hotspot-stage]");
    // Pointer devices open on hover. Touch has no hover, so tap still toggles.
    var canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    function cardFor(spot) {
      return spot.nextElementSibling;
    }

    /* A card is centred on its dot, which pushes it off-image for dots near the
       edges. Work out the correction from LAYOUT geometry, not the card's own
       rect: the card is mid-transition when this runs, so its measured rect is
       wherever the animation currently is rather than where it will land.
       The wrapper's rect and the card's offsetWidth are transform-free. */
    function clamp(spot) {
      var card = cardFor(spot);
      if (!card || !stage) return;

      var wrap = spot.parentElement.getBoundingClientRect();
      var s = stage.getBoundingClientRect();
      var cw = card.offsetWidth;
      var pad = 8;

      // Resting position: card centred on the dot (left:50% + translateX(-50%))
      var left = wrap.left + wrap.width / 2 - cw / 2;
      var right = left + cw;

      var shift = 0;
      if (left < s.left + pad) shift = s.left + pad - left;
      else if (right > s.right - pad) shift = s.right - pad - right;

      card.style.setProperty("--hs-shift", Math.round(shift) + "px");
    }

    function open(spot) {
      spots.forEach(function (s) {
        if (s !== spot) s.setAttribute("aria-expanded", "false");
      });
      spot.setAttribute("aria-expanded", "true");
      clamp(spot);
    }

    function closeAll() {
      spots.forEach(function (s) {
        s.setAttribute("aria-expanded", "false");
      });
    }

    spots.forEach(function (spot) {
      var wrap = spot.parentElement;

      if (canHover) {
        // Listen on the wrapper so moving from dot onto the card keeps it open
        wrap.addEventListener("mouseenter", function () {
          open(spot);
        });
        wrap.addEventListener("mouseleave", function () {
          spot.setAttribute("aria-expanded", "false");
        });
      }

      spot.addEventListener("click", function (e) {
        e.stopPropagation();
        if (spot.getAttribute("aria-expanded") === "true") closeAll();
        else open(spot);
      });

      // Keyboard parity with hover
      spot.addEventListener("focus", function () {
        open(spot);
      });
      spot.addEventListener("blur", function () {
        spot.setAttribute("aria-expanded", "false");
      });
    });

    document.addEventListener("click", closeAll);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeAll();
    });

    /* Clamp every card, not just the open one. A closed card is transparent but
       still laid out, so one centred on a dot near the right edge widened the
       whole document and let the page be dragged sideways on a phone. Runs at
       startup and after the stage image settles, since the correction is
       measured against the stage's box. */
    function clampAll() {
      spots.forEach(clamp);
    }

    window.addEventListener("resize", clampAll);
    clampAll();

    var stageImg = stage && stage.querySelector("img");
    if (stageImg && !stageImg.complete) {
      stageImg.addEventListener("load", clampAll);
    }
  }

  /* -------------------------------------------------------------------------
     8. Plan selector — keeps price, savings and CTA copy in sync
     ---------------------------------------------------------------------- */
  function initPlans() {
    var inputs = $$("[data-plan-input]");
    if (!inputs.length) return;

    var priceEl = $("[data-plan-price]");
    var noteEl = $("[data-plan-note]");
    var ctaEls = $$("[data-plan-cta]");
    // The sticky bar has far less room than the in-page CTA, so it carries its
    // own short label instead of overflowing on a 375px screen.
    var ctaShortEls = $$("[data-plan-cta-short]");
    // Shopify only: the hidden line-item inputs the product form actually posts.
    var variantInput = $("[data-variant-id]");
    var quantityInput = $("[data-quantity-id]");

    function sync() {
      var checked = inputs.filter(function (i) {
        return i.checked;
      })[0];
      if (!checked) return;

      if (variantInput && checked.getAttribute("data-variant")) {
        variantInput.value = checked.getAttribute("data-variant");
      }
      if (quantityInput && checked.getAttribute("data-qty")) {
        quantityInput.value = checked.getAttribute("data-qty");
      }

      var price = checked.getAttribute("data-price");
      var note = checked.getAttribute("data-note");
      var cta = checked.getAttribute("data-cta");
      var ctaShort = checked.getAttribute("data-cta-short") || cta;

      if (priceEl && price) priceEl.textContent = price;
      if (noteEl && note) noteEl.textContent = note;
      ctaEls.forEach(function (el) {
        if (cta) el.textContent = cta;
      });
      ctaShortEls.forEach(function (el) {
        if (ctaShort) el.textContent = ctaShort;
      });
    }

    inputs.forEach(function (i) {
      i.addEventListener("change", sync);
    });
    sync();
  }

  /* -------------------------------------------------------------------------
     8a. Direct buy (Shopify only)
     There is one product, so the cart is a step with nothing to decide in it.
     Submitting the buy form empties the cart, adds the selected bundle and
     goes straight to checkout — emptying first is what stops a second click,
     or a stale cart from an earlier visit, silently doubling the order.
     Without JS the form still posts to /cart/add with return_to=/checkout,
     which lands in the same place; only the de-duplication is lost.
     ---------------------------------------------------------------------- */
  function initDirectBuy() {
    var form = document.getElementById("norva-buy-form");
    if (!form || !window.fetch) return;

    var routes = window.routes || {};
    var cartUrl = routes.cart_url || "/cart";
    var addUrl = routes.cart_add_url || "/cart/add";
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    var checkoutUrl = root.replace(/\/$/, "") + "/checkout";
    var busy = false;

    // Both the in-page CTA and the sticky bar button submit this form.
    function buttons() {
      return $$('#norva-buy-form button[type="submit"], [form="norva-buy-form"]');
    }

    function setBusy(state) {
      busy = state;
      buttons().forEach(function (b) {
        b.disabled = state;
        b.setAttribute("aria-busy", state ? "true" : "false");
      });
    }

    form.addEventListener("submit", function (e) {
      if (busy) return;
      var variantInput = form.querySelector("[data-variant-id]");
      var id = variantInput && variantInput.value;
      if (!id) return; // let the plain POST handle it

      var quantityInput = form.querySelector("[data-quantity-id]");
      var qty = (quantityInput && parseInt(quantityInput.value, 10)) || 1;

      e.preventDefault();
      setBusy(true);

      var json = { "Content-Type": "application/json", Accept: "application/json" };

      fetch(cartUrl + "/clear.js", { method: "POST", headers: json })
        .then(function () {
          return fetch(addUrl + ".js", {
            method: "POST",
            headers: json,
            body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] }),
          });
        })
        .then(function (res) {
          if (!res.ok) throw new Error("add to cart failed: " + res.status);
          window.location.href = checkoutUrl;
        })
        .catch(function () {
          // Network or endpoint trouble — fall back to the ordinary form POST,
          // which carries return_to and still reaches checkout.
          setBusy(false);
          form.submit();
        });
    });
  }

  /* -------------------------------------------------------------------------
     8b. Delivery estimate
     A concrete date converts better than "ships in 24h". Dispatch is next
     working day, then 2 working days in transit — weekends skipped.
     NOTE: computed in the visitor's timezone, which is close enough for a
     shipping estimate. Move this server-side if you ever promise a cut-off.
     ---------------------------------------------------------------------- */
  function initDeliveryDate() {
    var els = $$("[data-delivery-date]");
    if (!els.length) return;

    var d = new Date();
    var workingDaysToAdd = 3;
    while (workingDaysToAdd > 0) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) workingDaysToAdd--;
    }

    var label = d.toLocaleDateString(document.documentElement.lang || "en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });

    els.forEach(function (el) {
      el.textContent = label;
      el.setAttribute("datetime", d.toISOString().slice(0, 10));
    });
  }

  /* -------------------------------------------------------------------------
     9. Count-up statistics
     ---------------------------------------------------------------------- */
  function initCounters() {
    var counters = $$("[data-count-to]");
    if (!counters.length) return;

    function paint(el, value) {
      var decimals = parseInt(el.getAttribute("data-decimals"), 10) || 0;
      el.textContent =
        (el.getAttribute("data-prefix") || "") +
        value.toLocaleString(document.documentElement.lang || "en", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }) +
        (el.getAttribute("data-suffix") || "");
    }

    if (reduced || !("IntersectionObserver" in window)) {
      counters.forEach(function (el) {
        paint(el, parseFloat(el.getAttribute("data-count-to")));
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          io.unobserve(el);
          var target = parseFloat(el.getAttribute("data-count-to"));
          var duration = 1400;
          var start = performance.now();
          (function tick(now) {
            var t = Math.min((now - start) / duration, 1);
            var eased = 1 - Math.pow(1 - t, 4); // ease-out quart
            paint(el, target * eased);
            if (t < 1) requestAnimationFrame(tick);
            else paint(el, target);
          })(start);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach(function (el) {
      paint(el, 0);
      io.observe(el);
    });
  }

  /* -------------------------------------------------------------------------
     10. Marquee — duplicate the track so the -50% loop is seamless
     ---------------------------------------------------------------------- */
  function initMarquee() {
    $$("[data-marquee]").forEach(function (track) {
      if (reduced) return;
      track.appendChild(track.firstElementChild.cloneNode(true));
    });
  }

  /* -------------------------------------------------------------------------
     11. Image fade-in (paired with width/height attrs, so no layout shift)
     ---------------------------------------------------------------------- */
  function initImages() {
    $$("img.img-fade").forEach(function (img) {
      if (img.complete) {
        img.classList.add("is-loaded");
      } else {
        img.addEventListener("load", function () {
          img.classList.add("is-loaded");
        });
        img.addEventListener("error", function () {
          img.classList.add("is-loaded");
        });
      }
    });
  }

  /* -------------------------------------------------------------------------
     12. Horizontal rails — native scroll-snap
     Several sections turn into a swipeable row on phones. Each rail is named
     via `data-rail="<id>"`; its optional controls point back at that id with
     `data-rail-prev/next/dots="<id>"`, so more than one rail can live on the
     page without their buttons crossing wires. Arrows are optional — on a
     phone the dots carry the affordance and the finger does the scrolling.
     ---------------------------------------------------------------------- */
  function initRail() {
    $$("[data-rail]").forEach(function (rail) {
      var id = rail.getAttribute("data-rail");
      var q = id ? '="' + id + '"' : "";
      var prev = $("[data-rail-prev" + q + "]");
      var next = $("[data-rail-next" + q + "]");
      var dotBox = $("[data-rail-dots" + q + "]");
      var dots = dotBox ? $$("[data-rail-dot]", dotBox) : [];

      function step() {
        var card = rail.firstElementChild;
        if (!card) return rail.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(rail).columnGap || "24") || 24;
        return card.getBoundingClientRect().width + gap;
      }

      function scrollByCards(dir) {
        rail.scrollBy({
          left: dir * step(),
          behavior: reduced ? "auto" : "smooth",
        });
      }

      if (prev) prev.addEventListener("click", function () { scrollByCards(-1); });
      if (next) next.addEventListener("click", function () { scrollByCards(1); });

      dots.forEach(function (dot, i) {
        dot.addEventListener("click", function () {
          rail.scrollTo({ left: i * step(), behavior: reduced ? "auto" : "smooth" });
        });
      });

      function sync() {
        var max = rail.scrollWidth - rail.clientWidth - 2;
        if (prev) prev.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= max;
        if (!dots.length) return;
        var i = Math.round(rail.scrollLeft / step());
        if (i < 0) i = 0;
        if (i > dots.length - 1) i = dots.length - 1;
        dots.forEach(function (d, n) {
          d.setAttribute("aria-current", n === i ? "true" : "false");
        });
      }

      rail.addEventListener("scroll", function () {
        requestAnimationFrame(sync);
      }, { passive: true });
      window.addEventListener("resize", sync);
      sync();
    });
  }

  /* -------------------------------------------------------------------------
     13. Newsletter — validate on blur, announce errors, then confirm
     NOTE: front-end only. Wire the submit handler to your real endpoint.
     ---------------------------------------------------------------------- */
  function initSignup() {
    var form = $("[data-signup]");
    if (!form) return;
    var input = $("input[type=email]", form);
    var error = $("[data-signup-error]", form);
    var done = $("[data-signup-done]");

    function validate() {
      var valid = input.checkValidity() && input.value.trim() !== "";
      if (!valid) {
        input.setAttribute("aria-invalid", "true");
        if (error) error.textContent = "Enter a valid email address, e.g. name@example.com";
      } else {
        input.removeAttribute("aria-invalid");
        if (error) error.textContent = "";
      }
      return valid;
    }

    input.addEventListener("blur", validate);
    input.addEventListener("input", function () {
      if (input.getAttribute("aria-invalid") === "true") validate();
    });

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (!validate()) {
        input.focus(); // a11y: focus-management — land on the invalid field
        return;
      }
      if (done) {
        form.hidden = true;
        done.hidden = false;
        done.focus();
      }
    });
  }

  /* -------------------------------------------------------------------------
     14. Current year in the footer
     ---------------------------------------------------------------------- */
  function initYear() {
    $$("[data-year]").forEach(function (el) {
      el.textContent = String(new Date().getFullYear());
    });
  }

  function boot() {
    initReveal();
    initHeroLines();
    initScrollChrome();
    initNav();
    initAccordion();
    initHotspots();
    initPlans();
    initDirectBuy();
    initDeliveryDate();
    initCounters();
    initMarquee();
    initImages();
    initRail();
    initSignup();
    initYear();
    document.documentElement.classList.add("js-ready");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
